import type { State } from '@/overmind';

type Match<S extends State, R = any> = {
  [Current in S['current']]: (
    current: S extends { current: Current } ? S : never
  ) => R;
};

export default function matchState<S extends State, T extends Match<S>>(
  state: S,
  matches: T & {
    [K in keyof T]: S extends State
      ? K extends S['current']
        ? T[K]
        : never
      : never;
  }
): {
  [K in keyof T]: T[K] extends (...args: any[]) => infer R ? R : never;
}[keyof T];
// eslint-disable-next-line no-redeclare
export default function matchState() {
  const state = arguments[0];
  const matches = arguments[1];

  if (matches) {
    return matches[state.current](state);
  }

  // @ts-ignore Too complex for TS to do this correctly
  return matches => matches[state.current](state);
}

type MatcherWithKey<T extends string, U> = {
  [key in T]: (value: key) => U;
};

type MatcherWithCases<T extends string, U, M> = {
  [key in T]: (input: Extract<M, { type: key }>) => U;
};

export class ForbiddenCaseMatched extends Error {
  constructor(case_: string) {
    super(`Forbidden case matched. Case: ${case_}`);
    this.name = 'ForbiddenCaseMatched';
  }
}

export const match = {
  asForbidden: (value: any) => {
    throw new ForbiddenCaseMatched(value);
  },
  allOf<T extends string, U>(value: T, matcher: MatcherWithKey<T, U>): U {
    const caseFn = matcher[value];
    if (caseFn) {
      return caseFn(value);
    } else {
      throw new Error(`Unmatched value: ${value}`);
    }
  },
  casesOf<T extends string, U, M extends { type: T }>(
    value: M,
    matcher: MatcherWithCases<T, U, M>
  ): U {
    const caseFn = matcher[value.type];
    if (caseFn) {
      return caseFn(value as unknown as Extract<M, { type: M['type'] }>);
    } else {
      throw new Error(`Unmatched value: ${value.type}`);
    }
  },
  someOf<T extends string, U>(
    value: T,
    matcher: Partial<MatcherWithKey<T, U>> & { default: () => U }
  ): U {
    const caseFn = matcher[value];
    if (caseFn) {
      return caseFn(value);
    } else if (matcher.default) {
      return matcher.default();
    } else {
      throw new Error(`Unmatched value: ${value}`);
    }
  }
} as const;
